/**
 * SpiceClientPicker - names the client a SPICE email is for, so the email
 * can file as the session note. The Email helper's SPICE face opens on
 * the drafting form and shows this picker only when the writer asks for
 * it ("Choose the client", or "Change client" once one is named). A
 * viewer who may open the client roster (canViewAnyClientRoster: every
 * client, or the viewer's engagement lane, as GET /v1/clients answers)
 * sees every SPICE client that read returns, searchable by name, her own
 * SPICE caseload first: coverage and emergencies need any of them. A
 * therapist with no roster sees her own caseload (assignment and cover)
 * from the home snapshot she already loads.
 *
 * No decrypting read on the all-clients path: `listSpiceClients` (GET
 * /v1/clients?view=spice) carries only structural SPICE facts, never a
 * plan line, so that picker leaves the topic blank until the writer
 * fills it in. For her own clients, a therapist's picker recovers the
 * plan from My week - the same decrypted text the band beside her
 * caseload already carries.
 */

import { type ReactNode, useMemo, useState } from 'react';
import { useCmHome } from '@/lib/cmHomeApi';
import { practiceTodayIso } from '@/lib/practiceDate';
import { useSpiceClientsList } from '@/lib/clientsApi';
import { Button } from '@/components/primitives/Button';
import { SearchField } from '@/components/primitives/SearchField';
import { ownClientsFirst } from '@/components/aiTools/ClientLookupField';
import {
  filterSpiceClientsByName,
  lastPlanFromMyWeek,
  type SpiceClientPickerRow,
} from '@/lib/spiceVisitPicker';

export interface SpiceClientPickerProps {
  tenantSlug: string;
  /** canViewAnyClientRoster: every SPICE client the viewer may open. */
  rosterOpen: boolean;
  /** A line seat: her own caseload, first in the list (and the list when
   *  rosterOpen is false), with each own client's last plan. */
  ownCaseload: boolean;
  onPick: (
    clientId: string,
    lastPlan: string | null,
    displayName: string,
    /** See SpiceClientPickerRow.contact_first_name — null from both of
     *  today's sources. */
    contactFirstName: string | null,
  ) => void;
}

const LABEL_CLS = 'block text-title-sm font-semibold text-sage-800';

export function SpiceClientPicker({ tenantSlug, rosterOpen, ownCaseload, onPick }: SpiceClientPickerProps): ReactNode {
  const todayIso = useMemo(() => practiceTodayIso(), []);
  const [query, setQuery] = useState('');

  const home = useCmHome(tenantSlug, todayIso, { enabled: ownCaseload });
  const spiceList = useSpiceClientsList(tenantSlug, { enabled: rosterOpen });

  // Normalized to the picker's own minimal row shape: the two source lists
  // (the tenant-wide roster read vs. the home snapshot's caseload) carry
  // different extra columns neither the search nor the list needs.
  // contact_first_name: null from both — neither source decrypts
  // client_contacts (no new decrypting read on this page).
  const own: SpiceClientPickerRow[] = (home.home?.clients ?? [])
    .filter((c) => c.is_spice_client === true)
    .map((c) => ({ id: c.id, display_name: c.display_name, contact_first_name: null }));
  const rows: SpiceClientPickerRow[] = rosterOpen
    ? ownClientsFirst(
        spiceList.clients.map((c) => ({ id: c.id, display_name: c.display_name, contact_first_name: null })),
        own,
      )
    : own;
  const isLoading = rosterOpen ? spiceList.isLoading : home.isLoading;
  const filtered = filterSpiceClientsByName(rows, query);

  function pick(clientId: string, displayName: string, contactFirstName: string | null): void {
    const lastPlan = ownCaseload ? lastPlanFromMyWeek(home.home?.my_week ?? null, clientId) : null;
    onPick(clientId, lastPlan, displayName, contactFirstName);
  }

  return (
    <div data-testid="spice-client-picker" className="space-y-3">
      <div>
        <label htmlFor="spice-client-picker-search" className={LABEL_CLS}>
          Which client is this visit for?
        </label>
        <SearchField
          id="spice-client-picker-search"
          value={query}
          onChange={setQuery}
          placeholder="Find a client"
          testId="spice-client-picker-search"
          className="mt-1.5"
        />
      </div>
      {isLoading ? (
        <p className="text-sm text-ink-soft" data-testid="spice-client-picker-loading">
          Loading your SPICE clients…
        </p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="spice-client-picker-empty">
          {rows.length === 0
            ? "Add SPICE Services for a client and you'll see them here."
            : 'Try another spelling.'}
        </p>
      ) : (
        <ul
          className="m-0 list-none divide-y divide-sage-100 rounded-[12px] border border-sage-400/20 p-0"
          data-testid="spice-client-picker-list"
        >
          {filtered.map((c) => (
            <li key={c.id}>
              <Button
                variant="ghost"
                className="w-full !justify-start !rounded-none px-3.5 py-2.5 text-sm font-normal"
                onClick={() => pick(c.id, c.display_name, c.contact_first_name)}
                data-testid="spice-client-picker-option"
              >
                {c.display_name}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
